import "@lit-labs/virtualizer";
import {
    type APIClientDTO,
    CompanyExport,
    type CompanyListing,
    companyStatusColor,
    companyStatusLabel,
    comparePropsFn,
    deserialize,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import "../../components/avatar/avatar";
import { loadEnabledFeatures } from "../../contexts/features-context";
import { app } from "../../init";
import { restart } from "../../lib/restart";
import { singleton } from "../../lib/singleton";
import { selectFile } from "../../lib/util";
import { ErrorHandling } from "../../mixins/error-handling";
import { type RouteArgs, routeProperty, Routing } from "../../mixins/routing";
import { ServiceWorker } from "../../mixins/service-worker";
import { StateMixin } from "../../mixins/state";
import { config, mixins, shared } from "../../styles";
import { alert, confirm } from "../alert-dialog";
import { Checkbox } from "../checkbox";
import { DateInput } from "../date-input";
import "../scroller";
import { type AdminList, adminRedirect, DEFAULT_ADMIN_LIST, isAdminList } from "./admin-route.util";
import "./api-client";
import "./api-client-secret-dialog";
import type { APIClientSecretDialog } from "./api-client-secret-dialog";
import { filterAPIClients } from "./api-client.util";
import "./company";
import "./company-filter-popover";
import type { CompanyFilterPopoverChangeEvent } from "./company-filter-popover";
import { applyCompanyFilter, type CompanyFilter } from "./company-filter.util";
import "./new-api-client-dialog";
import type { NewAPIClientDialog } from "./new-api-client-dialog";
import "./new-company-dialog";
import type { NewCompanyDialog } from "./new-company-dialog";

@customElement("ptc-admin-app")
export class AdminApp extends ServiceWorker(ErrorHandling(StateMixin(Routing(LitElement)))) {
    readonly routePattern = /^(?<list>[^/]*)(?:\/(?<id>[^/]+))?/;

    readonly active = true;

    @routeProperty({ arg: "list" })
    private _list: string | undefined;

    @routeProperty({ arg: "id" })
    private _selectedId: string | undefined;

    private get _listMode(): AdminList {
        return isAdminList(this._list) ? this._list : DEFAULT_ADMIN_LIST;
    }

    private get _selectedCompanyId(): number | undefined {
        return this._listMode === "companies" && this._selectedId ? Number(this._selectedId) : undefined;
    }

    private get _selectedClientId(): string | undefined {
        return this._listMode === "clients" ? this._selectedId : undefined;
    }

    @state()
    private _clients: APIClientDTO[] = [];

    @property({ type: Boolean, reflect: true, attribute: "singleton-container" })
    readonly singletonContainer = true;

    @state()
    private _loading = false;

    @state()
    private _loginError = "";

    @state()
    private _companies: CompanyListing[] = [];

    @state()
    private _filterString = "";

    @state()
    private _filter: CompanyFilter = {};

    @singleton("ptc-new-company-dialog")
    private _newCompanyDialog: NewCompanyDialog;

    @singleton("ptc-new-api-client-dialog")
    private _newAPIClientDialog: NewAPIClientDialog;

    @singleton("ptc-api-client-secret-dialog")
    private _apiClientSecretDialog: APIClientSecretDialog;

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    private _updateFilter() {
        this._filterString = this._filterInput.value.toLowerCase();
    }

    private _clearFilter() {
        this._filterString = this._filterInput.value = "";
    }

    constructor() {
        super();
        void app.loaded.then(() => this._load());
    }

    private async _loadClients() {
        try {
            this._clients = await app.api.listAPIClients();
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }
    }

    private async _login(e: FocusEvent) {
        e.preventDefault();

        const formData = new FormData(e.target as HTMLFormElement);

        this._loading = true;
        this._loginError = "";

        try {
            await app.login({
                email: formData.get("email") as string,
                password: formData.get("password") as string,
                scope: "admin",
            });
            await this.updateComplete;
            this._loading = false;
        } catch (e) {
            this._loading = false;
            this._loginError = e.message || "Anmeldung fehlgeschlagen. Bitte versuchen Sie es sp\u00e4ter noch einmal!";
            return;
        }

        void this._load();
    }

    private async _load() {
        if (!app.account) {
            return;
        }
        this._loading = true;

        try {
            this._companies = (await app.api.listCompanies()).sort(comparePropsFn("name"));
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }
        await this._loadClients();

        this._loading = false;

        this.routeChanged(this.router.path, this.router.params);
    }

    protected handleRoute(_args: RouteArgs, params: { [param: string]: string }, path: string) {
        const redirect = adminRedirect(path, params, this._list);
        if (redirect) {
            this.redirect(redirect);
        }
    }

    private async _logout() {
        const confirmed = await confirm("Sind Sie sicher dass Sie sich ausloggen möchten?", "Ausloggen");
        if (confirmed) {
            await app.logout();
            restart();
        }
    }

    private async _createAPIClient() {
        const created = await this._newAPIClientDialog.show();
        if (!created) return;

        await this._loadClients();
        this.go(`clients/${created.id}`);
        await this._apiClientSecretDialog.show(created);
    }

    private async _createCompany() {
        const company = await this._newCompanyDialog.show();

        if (company) {
            await this._load();
            this.go(`companies/${company.id}`);
        }
    }

    private async _importCompany() {
        const file = await selectFile();
        if (!file) {
            return;
        }
        const data = await file.text();
        const exp = deserialize(CompanyExport, JSON.parse(data));
        this._loading = true;
        try {
            const res = await app.api.importCompany(exp);
            await this._load();
            this.go(`companies/${res.companyId}`);
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    static styles = [
        config,
        shared,
        Checkbox.styles,
        DateInput.styles,
        css`
            :host {
                display: block;
                ${mixins.fullbleed()};
                background: var(--color-bg);
            }

            .login {
                ${mixins.fullbleed()};
                ${mixins.scroll()};
                z-index: 100;
                background: linear-gradient(#444, #111);
                color: var(--color-bg);
                padding: 2em;
            }

            .login .container {
                min-height: 100%;
                text-align: center;
            }

            .login h1 {
                margin: 0.5em;
            }

            .card {
                background: var(--color-bg);
                border-radius: calc(2 * var(--border-radius));
                box-shadow: rgba(0, 0, 0, 0.5) 0 0 10px -5px;
                position: relative;
                overflow: hidden;
            }

            .login .card {
                width: 300px;
                padding: 1em;
                color: var(--color-fg);
                margin: 2em;
            }

            .login .logo {
                height: 50px;
            }

            .login .greeting {
                margin-top: 1em;
                width: 350px;
                text-align: center;
            }

            .login input {
                text-align: inherit;
            }

            .login .message {
                margin: 0 0 0.8em 0;
            }

            .company-item {
                width: 100%;
                overflow: hidden;
            }

            .company-item.active {
                font-weight: bold;
                color: var(--blue);
                background: var(--blue-bg);
            }

            .list-mode {
                padding: 0 0.5em;
            }
        `,
    ];

    private _renderLogin() {
        return html`
            <div class="login">
                <div class="vertical center-aligning center-justifying layout container">
                    <ptc-ui-logo variant="full" class="logo"></ptc-ui-logo>

                    <h1>Admin</h1>

                    <div class="greeting">Willkommen zum Pentacode Admin Interface! Bitte melden Sie sich an.</div>

                    <div class="card">
                        <form class="vertical layout" @submit=${this._login} ?disabled=${this._loading}>
                            <div class="field">
                                <input type="email" name="email" placeholder="E-mail Adresse" />
                            </div>

                            <div class="field">
                                <input type="password" name="password" placeholder="Passwort" />
                            </div>

                            <div class="negative message" ?hidden=${!this._loginError}>${this._loginError}</div>

                            <button type="submit" class="primary">Anmelden</button>
                        </form>
                    </div>
                </div>
            </div>
        `;
    }

    private _renderClientList() {
        return html`
            <lit-virtualizer
                class="stretch relative"
                scroller
                .items=${filterAPIClients(this._clients, this._filterString)}
                .renderItem=${(c: APIClientDTO) => html`
                    <div
                        class="click ${this._selectedClientId === c.id
                            ? "active"
                            : ""} padded border-bottom company-item"
                        @click=${() => this.go(`clients/${c.id}`)}
                    >
                        <div class="stretch ellipsis bottom-margined collapse">${c.name}</div>
                        <div class="tiny subtle horizontal spacing layout">
                            <div class="pill">${c.accesses.length} Unternehmen</div>
                            ${c.partnerScopes.map((scope) => html`<div class="pill">${scope}</div>`)}
                        </div>
                    </div>
                `}
            >
            </lit-virtualizer>
        `;
    }

    private _renderMain() {
        const selectedClient = this._clients.find((c) => c.id === this._selectedClientId) ?? null;
        return html`
            <div class="fullbleed horizontal layout stretch collapse">
                <div class="vertical layout border-right">
                    <div class="horizontal tabs list-mode border-bottom">
                        <button ?active=${this._listMode === "companies"} @click=${() => this.go("companies")}>
                            Unternehmen
                        </button>
                        <button ?active=${this._listMode === "clients"} @click=${() => this.go("clients")}>
                            API-Clients
                        </button>
                    </div>
                    <div class="padded center-aligning horizontal layout">
                        <button class="smaller skinny transparent right-margined">
                            <ptc-ui-icon name="icon.action.filter"></ptc-ui-icon>
                        </button>
                        <ptc-company-filter-popover
                            @change=${(e: CompanyFilterPopoverChangeEvent) => {
                                this._filter = e.detail;
                            }}
                        ></ptc-company-filter-popover>
                        <div class="filter-input right icon input stretch right-margined">
                            <input id="filterInput" type="text" placeholder="Suchen..." @input=${this._updateFilter} />
                            ${this._filterString
                                ? html`<ptc-ui-icon
                                      name="icon.action.clear"
                                      class="click icon"
                                      @click=${this._clearFilter}
                                  ></ptc-ui-icon>`
                                : html`<ptc-ui-icon
                                      name="icon.action.search"
                                      class="icon"
                                      @click=${this._clearFilter}
                                  ></ptc-ui-icon>`}
                        </div>
                        ${this._listMode === "companies"
                            ? html`
                                  <button
                                      class="slim transparent"
                                      @click=${this._importCompany}
                                      ?disabled=${!loadEnabledFeatures().companyImport}
                                      title=${loadEnabledFeatures().companyImport ? "" : "Aktuell nicht verfügbar"}
                                  >
                                      <ptc-ui-icon name="icon.action.import"></ptc-ui-icon>
                                  </button>
                                  <button class="slim transparent" @click=${this._createCompany}>
                                      <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                                  </button>
                              `
                            : html`
                                  <button class="slim transparent" @click=${this._createAPIClient}>
                                      <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                                  </button>
                              `}
                        <button class="slim transparent" @click=${this._logout}>
                            <ptc-ui-icon name="icon.action.log-out"></ptc-ui-icon>
                        </button>
                    </div>

                    ${this._listMode === "clients" ? this._renderClientList() : this._renderCompanyList()}
                </div>

                <div class="stretch relative">
                    ${this._listMode === "clients"
                        ? html`
                              <ptc-admin-api-client
                                  class="fullbleed"
                                  .client=${selectedClient}
                                  .companies=${this._companies}
                                  @changed=${this._loadClients}
                              ></ptc-admin-api-client>
                          `
                        : html`
                              <ptc-admin-company
                                  class="fullbleed"
                                  .companyId=${this._selectedCompanyId}
                                  @updated=${this._load}
                              ></ptc-admin-company>
                          `}
                </div>
            </div>
        `;
    }

    private _renderCompanyList() {
        return html`
            <lit-virtualizer
                class="stretch relative"
                scroller
                .items=${applyCompanyFilter(this._companies, this._filter).filter(
                    (c) =>
                        c.name.toLowerCase().includes(this._filterString) ||
                        c.id.toString().includes(this._filterString) ||
                        c.venues.some((v) => v.name.toLowerCase().includes(this._filterString))
                )}
                .renderItem=${(c: CompanyListing) => html`
                    <div
                        class="click ${this._selectedCompanyId === c.id
                            ? "active"
                            : ""} padded border-bottom company-item"
                        @click=${() => this.go(`companies/${c.id}`)}
                    >
                        <div class="horizontal spacing center-aligning layout bottom-margined">
                            <div class="stretch ellipsis collapse">
                                ${c.name} <span class="subtle"> (${c.id})</span>
                            </div>
                            <div class="tiny pill" style="--color-highlight: ${companyStatusColor(c.effectiveStatus)}">
                                ${companyStatusLabel(c.effectiveStatus)}
                            </div>
                            ${c.isBetaCustomer
                                ? html`<div class="tiny pill" style="--color-highlight: purple">Beta</div>`
                                : nothing}
                        </div>
                        <div class="tiny subtle horizontal spacing layout">
                            ${c.venues.length
                                ? c.venues.map(
                                      (v) => html` <div class="pill ellipsis" style="max-width: 10em">${v.name}</div> `
                                  )
                                : html`<div class="pill">[ Ohne Standorte ]</div>`}
                        </div>
                    </div>
                `}
            >
            </lit-virtualizer>
        `;
    }

    render() {
        return html`
            ${!app.account ? this._renderLogin() : this._renderMain()}

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>

            <ptc-new-company-dialog></ptc-new-company-dialog>
            <ptc-new-api-client-dialog></ptc-new-api-client-dialog>
            <ptc-api-client-secret-dialog></ptc-api-client-secret-dialog>

            <slot></slot>
        `;
    }
}
